import {
  ApolloClient,
  DocumentNode,
  NormalizedCacheObject,
  gql,
} from '@apollo/client';

import { insertCohortMembers, updateCohortMember } from 'queries/programmes';

export const getEventProgrammeMembershipContext = gql`
  query getEventProgrammeMembershipContext($eventId: uuid!, $userId: uuid!) {
    event: event_by_pk(id: $eventId) {
      id
      cohorts {
        id
      }
      detail {
        programme_id
        programme {
          id
          title
          standardCohorts: cohorts(
            where: { standard: { _eq: true } }
            limit: 1
          ) {
            id
          }
          cohorts {
            id
            standard
            members(
              where: {
                _and: [
                  { member_id: { _eq: $userId } }
                  { type: { _eq: "STUDENT" } }
                  { archived: { _eq: false } }
                ]
              }
            ) {
              id
              event_id
            }
          }
        }
      }
    }
  }
`;

export type EnsureProgrammeMembershipResult = {
  skipped: boolean;
  reason?: string;
  created: boolean;
  updated: boolean;
  programmeTitle?: string | null;
  cohortMemberIds: string[];
};

type ApolloLike = Pick<ApolloClient<NormalizedCacheObject>, 'query' | 'mutate'>;

/**
 * After adding a student to an on-site event that has an underlying programme:
 * - if they are already on that programme, set cohort_member.event_id to this event
 * - if not, add them to a cohort for that programme and link this event
 */
export async function ensureProgrammeMembershipForEvent(
  client: ApolloLike,
  {
    userId,
    eventId,
    seatType = 'STUDENT',
  }: {
    userId: string;
    eventId: string;
    seatType?: string | null;
  },
): Promise<EnsureProgrammeMembershipResult> {
  if (!userId || !eventId) {
    return {
      skipped: true,
      reason: 'missing-ids',
      created: false,
      updated: false,
      cohortMemberIds: [],
    };
  }

  if (seatType && seatType !== 'STUDENT') {
    return {
      skipped: true,
      reason: 'not-student',
      created: false,
      updated: false,
      cohortMemberIds: [],
    };
  }

  const { data } = await client.query({
    query: getEventProgrammeMembershipContext,
    variables: { eventId, userId },
    fetchPolicy: 'network-only',
  });

  const event = data?.event;
  const programme = event?.detail?.programme;
  const programmeId = event?.detail?.programme_id;

  if (!programmeId || !programme) {
    return {
      skipped: true,
      reason: 'no-programme',
      created: false,
      updated: false,
      cohortMemberIds: [],
    };
  }

  const existingMembers = (programme.cohorts || []).flatMap(
    (cohort: { members?: { id: string; event_id?: string | null }[] }) =>
      cohort.members || [],
  );

  if (existingMembers.length > 0) {
    const ids: string[] = [];
    for (const member of existingMembers) {
      if (member.event_id === eventId) {
        ids.push(member.id);
        continue;
      }
      await client.mutate({
        mutation: updateCohortMember as DocumentNode,
        variables: {
          id: member.id,
          data: { event_id: eventId },
        },
      });
      ids.push(member.id);
    }

    return {
      skipped: false,
      created: false,
      updated: true,
      programmeTitle: programme.title,
      cohortMemberIds: ids,
    };
  }

  const cohortId =
    event.cohorts?.[0]?.id ||
    programme.standardCohorts?.[0]?.id ||
    programme.cohorts?.[0]?.id;

  if (!cohortId) {
    return {
      skipped: true,
      reason: 'no-cohort',
      created: false,
      updated: false,
      programmeTitle: programme.title,
      cohortMemberIds: [],
    };
  }

  const insertResult = await client.mutate({
    mutation: insertCohortMembers as DocumentNode,
    variables: {
      data: [
        {
          member_id: userId,
          cohort_id: cohortId,
          event_id: eventId,
          type: 'STUDENT',
        },
      ],
    },
  });

  const insertedIds =
    insertResult?.data?.insert_cohort_member?.returning?.map(
      (row: { id: string }) => row.id,
    ) || [];

  return {
    skipped: false,
    created: true,
    updated: false,
    programmeTitle: programme.title,
    cohortMemberIds: insertedIds,
  };
}
